<template lang="pug" id="timeline">
extends ./Base.pug
</template>

<script>
import * as d3 from 'd3';
import timelines from '@agentiq/d3-timelines';
import BaseChart from './Base.vue';
import isEmpty from 'lodash/isEmpty';

const TOOLTIP_BOX_WIDTH = 390;
const TOOLTIP_BOX_OFFSET = 20;
const suppressedEvents = [
  { eventType: 'click', preventDefault: true },
  { eventType: 'dblclick', preventDefault: true },
  { eventType: 'pointerdown', preventDefault: true },
  { eventType: 'pointerup', preventDefault: true },
  { eventType: 'wheel', preventDefault: false },
];

export default {
  extends: BaseChart,
  name: 'aiqTimelineChart',
  computed: {
    colorScale() {
      const { domain, range } = this.graphParams;
      return d3.scaleOrdinal(domain, range);
    },
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.renderChart);
    });
  },
  beforeUnmount() {
    this.$nextTick(() => {
      window.removeEventListener('resize', this.renderChart);
      const chartContainer = this.$el.querySelector('.chart-container');
      if (chartContainer) {
        suppressedEvents.forEach((({ eventType }) => {
          chartContainer.removeEventListener(eventType, () => {});
        }));
      }
    });
  },
  methods: {
    getChartType() {
      return 'timelineChart';
    },
    setChart() {
      const { colorProperty, timeRange: { beginning, ending }, layout: { chartMargins } } = this.graphParams;
      const { top, bottom, left, right } = chartMargins;
      this.chart = timelines()
        .stack()
        .colors(this.colorScale)
        .colorProperty(colorProperty)
        .margin({ left, right, top, bottom })
        .beginning(beginning)
        .ending(ending)
        .tickFormat({
          format: function(d) { return d3.timeFormat('%I %p')(d) },
          tickTime: d3.timeHour,
          tickInterval: 1,
          tickSize: 6,
        })
        .mouseover((event, d, i) => {
          const tooltipElement = d3
            .select(this.chartContainerSelector())
            .select(`.timeline-tooltip-${this.report[i].className}`);

          // Remove slot intervals if previously appended
          tooltipElement
            .select('.tooltip-header')
            .selectAll('.slot-interval')
            .remove();

          // Append slot interval
          const intervalFormat = '%-I:%M%p';
          const slotStart = d3.timeFormat(intervalFormat)(d.starting_time);
          const slotEnd = d3.timeFormat(intervalFormat)(d.ending_time);
          tooltipElement
            .select('.tooltip-header')
            .append('div')
            .attr('class', 'slot-interval')
            .text(`(${slotStart} - ${slotEnd})`);

          // set left and top of the tooltip box based on the mouse event coordinates and window size
          // to ensure the box is close the mouse event but doesn't get cropped out of the screen
          const horizontalCenter = Math.floor(window.innerWidth / 2); // eslint-disable-line
          const tooltipBounds = tooltipElement.node().getBoundingClientRect();
          const left = event.pageX < horizontalCenter ?
            `${event.pageX + TOOLTIP_BOX_OFFSET}px` :
            `${event.pageX - TOOLTIP_BOX_WIDTH - TOOLTIP_BOX_OFFSET}px`;
          const top = `${event.pageY - (tooltipBounds.height / 2)}px`; // eslint-disable-line

          tooltipElement
            .style('left', left)
            .style('top', top)
            .style('visibility', 'visible');
        })
        .mouseout((_, __, i) => {
          d3
            .select(this.chartContainerSelector())
            .select(`.timeline-tooltip-${this.report[i].className}`)
            .style('visibility', 'hidden');
        });
    },
    setSVG() {
      const { layout: { row, axis, chartMargins } } = this.graphParams;
      const { top, bottom } = chartMargins;
      const numberOfRows = this.report.length;
      const chartHeight = top + bottom + numberOfRows * (row.height + row.margin) + axis.height;
      d3
        .select(this.chartContainerSelector())
        .append('svg')
        .attr('width', '100%')
        .attr('height', chartHeight)
        .datum(this.report)
        .call(this.chart);
    },
    removeChart() {
      this.DestroyChart();
      d3
        .select(this.chartContainerSelector())
        .selectAll('svg')
        .remove();
    },
    createTooltips() {
      for (const { className, tooltipLabel, tooltipData } of this.report) {
        // empty timelines don't have tooltip data because they don't trigger the hover event
        if (tooltipData) {
          const { items, footer } = tooltipData;
          // create tooltip skeleton
          const tooltip = d3
            .select(this.chartContainerSelector())
            .append('div')
            .attr('class', `timeline-tooltip-${className}`)
            .style('width', `${TOOLTIP_BOX_WIDTH}px`)
            .style('visibility', 'hidden');
          tooltip
            .append('div')
            .attr('class', 'tooltip-header');
          tooltip
            .append('div')
            .attr('class', 'tooltip-body');
          tooltip
            .append('div')
            .attr('class', 'tooltip-footer');

          // set tooltip header
          tooltip
            .select('.tooltip-header')
            .append('div')
            .text(tooltipLabel);

          // set tooltip data
          const tooltipBody = tooltip.select('.tooltip-body');
          for (const dataItem of items) {
            const tooltipRow = tooltipBody
              .append('div')
              .attr('class', 'row');
            tooltipRow
              .append('div')
              .append('div')
              .attr('class', 'color-square')
              .style('background', dataItem.color);
            tooltipRow
              .append('div')
              .attr('class', 'status-text')
              .text(dataItem.status);
            tooltipRow
              .append('div')
              .text(dataItem.duration);
          }

          // set tooltip footer
          const tooltipFooter = tooltip
            .select('.tooltip-footer');
          tooltipFooter
            .append('div')
            .text(footer.label);
          tooltipFooter
            .append('div')
            .text(footer.totalTime);
        }
      }
    },
    renderChart() {
      this.removeChart();
      if (!isEmpty(this.report)) {
        this.setChart();
        this.setSVG();
        this.createTooltips();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.chart-container) {
  height: unset;

  svg {
    background: #fff;
    padding-left: 5px;
  }

  div[class^='timeline-tooltip'] {
    position: fixed;
    border: 1px solid #A8A9AB;
    border-radius: 4px;
    background-color: #fff;
    color: #000;
    padding: 8px;
    z-index: 2;

    .tooltip-header {
      display: flex;
      margin-bottom: 5px;
      justify-content: space-between;
      flex-wrap: wrap;
      word-break: break-word;

      .slot-interval {
        font-size: 12px;
        color: #6B6D70;
      }
    }

    .tooltip-body {
      font-size: 12px;

      .row {
        display: flex;
        width: 100%;
        align-items: center;

        div:nth-child(1) {
          width: 5%;
          display: flex;
          align-items: center;

          .color-square {
            height: 10px;
            width: 10px;
          }
        }

        div:nth-child(2) {
          width: 75%;

          .status-text {
            display: inline-block;
            width: 240px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        div:nth-child(3) {
           width: 20%;
        }
      }
    }

    .tooltip-footer {
      font-size: 12px;
      display: flex;
      width: 100%;

      div:nth-child(1) {
        width: 80%;
      }

      div:nth-child(2) {
        width: 20%;
      }
    }

  }
}
</style>